import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Printer } from "lucide-react";
import { usePortalAuth } from "@/components/portal/PortalAuth";
import { site, fullAddress } from "@/config/site";
import { SEO } from "@/components/site/SEO";
import { fmtDay, gbp2 } from "@/components/portal/shared";

export default function OrderSheetPrintPage() {
  const { id } = useParams();
  const { user, api } = usePortalAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (user === false) navigate("/portal/login", { replace: true });
  }, [user, navigate]);

  useEffect(() => {
    if (user && user.role !== "installer")
      api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => setMissing(true));
  }, [user, api, id]);

  if (user === false || (user && user.role === "installer")) return null;
  if (missing) return <p className="p-10 text-sm text-red-700" role="alert">Order not found.</p>;
  if (!order)
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-navy-900" />
      </div>
    );

  const rooms = order.lead?.measurement_rooms || [];
  const installLine = [order.install_date ? fmtDay(order.install_date) : "", order.install_time, order.installer].filter(Boolean).join(" · ") || "Not scheduled";

  return (
    <div className="min-h-screen bg-warm-50 py-8 px-4 print:bg-white print:py-0" data-testid="order-sheet-page">
      <SEO title={`Order Sheet ${order.ref} | My Blinds Company`} description="Order worksheet." path={`/portal/orders/${id}/sheet`} />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-6 no-print">
          <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-medium text-navy-900">
            <ArrowLeft className="h-4 w-4" /> Back to order
          </button>
          <button type="button" data-testid="sheet-print-btn" onClick={() => window.print()} className="inline-flex items-center gap-2 rounded-full bg-navy-900 px-6 py-2.5 text-xs font-semibold text-white hover:bg-navy-800">
            <Printer className="h-4 w-4" /> Print / Save as PDF
          </button>
        </div>

        <div className="rounded-[1.5rem] border border-warm-200 bg-white shadow-xl overflow-hidden print:shadow-none print:border-0 print:rounded-none" data-testid="order-sheet-document">
          <div className="bg-navy-950 text-white p-8 print:bg-white print:text-slate-900 print:border-b print:border-warm-200">
            <div className="flex items-start justify-between gap-4">
              <div>
                <img src="/logo.png" alt="My Blinds Company logo" className="h-11 w-auto rounded bg-white p-0.5 mb-3" />
                <p className="text-xs text-white/60 print:text-slate-500">{site.legalName}<br />{fullAddress} · {site.phone.display}</p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-[0.25em] text-white/50 print:text-slate-500 mb-1">Order Sheet</p>
                <h1 className="font-serif text-3xl" data-testid="sheet-ref">{order.ref}</h1>
                <p className="text-xs text-white/60 print:text-slate-500 mt-2">{new Date(order.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}</p>
              </div>
            </div>
          </div>

          <div className="p-8">
            <div className="grid sm:grid-cols-2 gap-6 mb-7">
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Customer</p>
                <p className="text-sm font-semibold text-slate-900" data-testid="sheet-customer">{order.customer}</p>
                <p className="text-xs text-slate-500">{[order.address1, order.town, order.postcode].filter(Boolean).join(", ") || "—"}</p>
                <p className="text-xs text-slate-500">{order.phone || ""}</p>
              </div>
              <div>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Installation</p>
                <p className="text-sm font-semibold text-slate-900" data-testid="sheet-install">{installLine}</p>
                {order.sales_rep && <p className="text-xs text-slate-500">Sales rep: {order.sales_rep}</p>}
              </div>
            </div>

            <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Items</p>
            <table className="w-full text-sm mb-7">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-warm-200">
                  <th className="pb-2 font-semibold">Item</th>
                  <th className="pb-2 font-semibold text-center">Qty</th>
                  <th className="pb-2 font-semibold text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(order.items || []).map((i, x) => (
                  <tr key={x} className="border-b border-warm-200/70">
                    <td className="py-2.5 text-slate-800">{i.description}</td>
                    <td className="py-2.5 text-center text-slate-600">{i.qty}</td>
                    <td className="py-2.5 text-right text-slate-800">{gbp2(i.price * i.qty)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {rooms.length > 0 && (
              <>
                <p className="text-[11px] uppercase tracking-wider text-slate-400 mb-1.5">Measurements</p>
                <table className="w-full text-xs mb-7" data-testid="sheet-measurements">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b border-warm-200">
                      <th className="pb-2 pr-2 font-semibold">Room / Window</th>
                      <th className="pb-2 pr-2 font-semibold">Type</th>
                      <th className="pb-2 pr-2 font-semibold text-center">Width</th>
                      <th className="pb-2 pr-2 font-semibold text-center">Drop</th>
                      <th className="pb-2 pr-2 font-semibold text-center">Unit</th>
                      <th className="pb-2 pr-2 font-semibold">Control</th>
                      <th className="pb-2 pr-2 font-semibold">Material</th>
                      <th className="pb-2 font-semibold">Fitting</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.flatMap((r) => r.windows.map((w) => ({ r, w }))).map(({ r, w }, x) => (
                      <tr key={x} className="border-b border-warm-200/60">
                        <td className="py-2 pr-2 text-slate-800">{w.ref || r.name}</td>
                        <td className="py-2 pr-2 text-slate-600">{w.blindType || "—"}</td>
                        <td className="py-2 pr-2 text-center text-slate-800">{w.width || "—"}</td>
                        <td className="py-2 pr-2 text-center text-slate-800">{w.drop || "—"}</td>
                        <td className="py-2 pr-2 text-center text-slate-600">{w.unit || "mm"}</td>
                        <td className="py-2 pr-2 text-slate-600">{w.controlSide || "—"}</td>
                        <td className="py-2 pr-2 text-slate-600">{[w.material, w.colour].filter(Boolean).join(" ") || "—"}</td>
                        <td className="py-2 text-slate-600">{w.sizeType || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}

            <div className="flex justify-between text-lg font-semibold text-slate-900 border-t border-warm-200 pt-3">
              <span>Order total</span><span data-testid="sheet-total">{gbp2(order.total)}</span>
            </div>
            <p className="mt-6 text-[11px] text-slate-400">{site.legalName} · {fullAddress} · {site.phone.display}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
